import type { ActiveUpload } from '@/shared/stores/upload-store';
import {
  describeProcessingFailure,
  type ProcessingFailureReason,
} from '@/shared/utils/bunny-failure';
import type { ProcessingStatus, StudioVideoListItem } from '@/types/studio';
import {
  BUNNY_FAILED_STATUSES,
  BUNNY_NOT_READY_STATUSES,
  BUNNY_PROCESSING_STATUSES,
  BUNNY_UPLOADING_STATUSES,
} from '@/types/studio';

const SLOW_UPLOAD_ETA_SECONDS = 1_200;
const SLOW_UPLOAD_MIN_BYTES = 500 * 1024 * 1024;

export type UploadFailureReason =
  'upload_session_expired' | 'upload_conflict' | 'upload_network' | 'upload_unknown';

export type FailureReason = ProcessingFailureReason | UploadFailureReason;

export interface FailureDetail {
  reason: FailureReason;
  /** Row / card header: "Processing failed" vs "Upload interrupted". */
  title: string;
  message: string;
  hint?: string;
  canRetry: boolean;
}

/**
 * The one vocabulary every studio surface renders from. Visible phases are
 * unchanged (Preparing → Uploading → Processing → Ready, Failed, Stale); what
 * changed is that nobody downstream re-derives them from `bunny_status`,
 * store phases or message text.
 */
export type UploadState =
  | { kind: 'preparing' }
  | { kind: 'uploading'; reconnecting: boolean; slow: boolean }
  | { kind: 'processing'; pollLost: boolean }
  | { kind: 'ready' }
  | { kind: 'stale' }
  | ({ kind: 'failed' } & FailureDetail);

export type UploadStateKind = UploadState['kind'];

/**
 * Copy for `processing` + `pollLost`. The client lost the API, not the video:
 * Bunny keeps encoding while the poller is blind, so this is never a failure
 * and never reaches the failure catalog (FRONT-355).
 */
export const POLL_LOST_COPY = {
  title: 'Still processing',
  message:
    "Can't reach the server right now — your video is still processing. We'll keep checking.",
} as const;

/**
 * Copy for the creator-initiated status check. Lives here rather than at the
 * call site so every surface that grows the control says the same thing —
 * same rule as the failure catalog.
 *
 * `throttled` exists because the BE caps this at 6/min per video; a creator
 * watching a slow encode will press it more often than that.
 */
export const MANUAL_REFRESH_COPY = {
  action: 'Check status',
  pending: 'Checking…',
  throttled: 'Just checked — try again shortly.',
  /**
   * Friendly, but never false. The failure line still reads warmly; it just
   * does not tell a creator to keep waiting for a video that will not arrive.
   */
  toast: {
    moved: 'Good news — your video moved along. It should be ready shortly.',
    stillGoing: "All good — it's still encoding. Hang tight, we'll update this for you.",
    failed: "This one didn't make it through encoding. Try uploading it again.",
    unavailable: "Couldn't reach the server just now — your video is still processing.",
  },
} as const;

/** Processing status as the API alone can tell it (no live upload client). */
export function deriveApiProcessingStatus(video: StudioVideoListItem): ProcessingStatus {
  // Non-Bunny legacy videos: published with published_at and uuid ⇒ ready.
  if (!video.is_bunny_video && video.published === true && video.published_at && video.uuid) {
    return 'ready';
  }
  const status = video.bunny_status;
  if (status !== undefined && BUNNY_FAILED_STATUSES.has(status)) return 'failed';
  if (status !== undefined && (status === 0 || BUNNY_UPLOADING_STATUSES.has(status))) {
    return 'uploading';
  }
  // Blocklist, not allowlist: anything outside the known not-ready set
  // (incl. 11–13 and future post-ready states) is ready regardless of `processing`.
  if (status !== undefined && !BUNNY_NOT_READY_STATUSES.has(status)) return 'ready';
  if (video.processing === true) return 'processing';
  if (status !== undefined && BUNNY_PROCESSING_STATUSES.has(status)) return 'processing';
  return 'ready';
}

/** Upload-side (TUS / dispatch) failures. Processing failures never come through here. */
export function describeUploadFailure(error: string | null): {
  reason: UploadFailureReason;
  message: string;
} {
  const lower = (error ?? '').toLowerCase();
  if (lower.includes('expir') || lower.includes('404') || lower.includes('session')) {
    return {
      reason: 'upload_session_expired',
      message: 'Upload session expired. Please try again.',
    };
  }
  if (lower.includes('conflict') || lower.includes('423') || lower.includes('locked')) {
    return { reason: 'upload_conflict', message: 'Upload conflict detected. Please try again.' };
  }
  if (lower.includes('network') || lower.includes('offline') || lower.includes('timeout')) {
    return { reason: 'upload_network', message: 'Upload interrupted. Check your connection.' };
  }
  return { reason: 'upload_unknown', message: 'Upload failed. Please try again.' };
}

function processingFailure(bunnyLastError: string | null | undefined): FailureDetail {
  const { reason, message, hint } = describeProcessingFailure(bunnyLastError);
  return {
    reason,
    title: 'Processing failed',
    message,
    ...(hint !== undefined ? { hint } : {}),
    canRetry: false,
  };
}

function uploadFailure(entry: ActiveUpload): FailureDetail {
  const { reason, message } = describeUploadFailure(entry.error);
  return {
    reason,
    title: 'Upload interrupted',
    message,
    canRetry: !entry.circuitBreaker.isOpen,
  };
}

function isSlow(entry: ActiveUpload): boolean {
  return (
    entry.etaSeconds !== undefined &&
    entry.etaSeconds > SLOW_UPLOAD_ETA_SECONDS &&
    entry.bytesTotal > SLOW_UPLOAD_MIN_BYTES
  );
}

export interface DeriveUploadStateInput {
  /** Row from the studio list endpoint; null for store-only surfaces (modal card, indicator). */
  api?: StudioVideoListItem | null;
  /** Live upload-store entry owning this video; null when no client owns it. */
  entry?: ActiveUpload | null;
  /** Row-level stale signal (hydrated entry with no live TUS client). */
  isStale?: boolean;
}

/**
 * Precedence, written once:
 * 1. stale wins (explicit flag or entry.isStale)
 * 2. API "ready" wins — the list endpoint is authoritative for playability
 * 3. a live entry owns every in-flight phase and its own failure
 * 4. API terminal failure (bunny_status 5/8) with the encoder string
 * 5. API "uploading" with no owner ⇒ ghost row ⇒ stale
 */
export function deriveUploadState(input: DeriveUploadStateInput): UploadState {
  const api = input.api ?? null;
  const entry = input.entry ?? null;
  if (input.isStale || entry?.isStale) return { kind: 'stale' };

  const apiStatus = api ? deriveApiProcessingStatus(api) : null;
  if (apiStatus === 'ready') return { kind: 'ready' };

  if (entry) {
    switch (entry.phase) {
      case 'preparing':
        return { kind: 'preparing' };
      case 'uploading':
      case 'reconnecting':
        return {
          kind: 'uploading',
          reconnecting: entry.phase === 'reconnecting',
          slow: isSlow(entry),
        };
      case 'processing':
        return { kind: 'processing', pollLost: entry.pollLost === true };
      case 'error':
        // Bunny's string is authoritative when the list already has it; the
        // poller's copy of it (or the neutral fallback) covers the lag window.
        return entry.failureStage === 'processing'
          ? { kind: 'failed', ...processingFailure(api?.bunny_last_error ?? entry.error) }
          : { kind: 'failed', ...uploadFailure(entry) };
      case 'complete':
      case 'idle':
        break;
    }
  }

  if (apiStatus === 'failed') {
    return { kind: 'failed', ...processingFailure(api?.bunny_last_error) };
  }
  if (apiStatus === 'uploading') {
    return entry ? { kind: 'uploading', reconnecting: false, slow: false } : { kind: 'stale' };
  }
  if (apiStatus === 'processing') return { kind: 'processing', pollLost: false };
  return { kind: 'ready' };
}
